// Shared survey attribute-tree helpers.
//
// The survey `data` tree's leaf `type` set is OPEN-ENDED. Beyond the types the
// AttributeField union declares (node/input/select/area/multiSelect/photos), the
// runtime data also carries "date", "number", "text", "switch", "boolean",
// "image", "matterport", "blob", plus structural "page"/"ellipsis" containers.
// Code that switched on a fixed list of type names silently dropped filled
// values of the unlisted types (a filled "Site Visit Date" of type "date" read
// as blank). And code that recursed only on `type === "node"` skipped the
// children of non-"node" containers. These helpers key off the value SHAPE and
// the presence of child `fields`, so every reader stays correct as the schema
// grows and agrees with the survey viewer / export tree-walker.

type SurveyFieldish = { type?: string; value?: unknown; fields?: unknown };

/**
 * Child fields when the field is a container, else null (it's a leaf question).
 * A `type: "node"` is always a container (even with an empty child list); any
 * other field carrying a populated `fields[]` is also a container — grouping
 * fields don't always declare `type === "node"` (mirrors the export
 * `getChildren` tree-walker).
 */
export function getSurveyChildren<T extends SurveyFieldish>(field: T): T[] | null {
  const fields = field.fields;
  if (field.type === "node") return (Array.isArray(fields) ? fields : []) as T[];
  return Array.isArray(fields) && fields.length > 0 ? (fields as T[]) : null;
}

/**
 * Normalized scalar string for a leaf's value. "" when empty/absent. Arrays are
 * joined (blanks dropped); `area` HTML is stripped to plain text; numbers and
 * booleans are stringified. Mirrors how the survey viewer decides a field shows
 * a value.
 */
export function getSurveyLeafValueString(field: SurveyFieldish): string {
  const value = field.value;
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value
      .filter((v) => v != null && (typeof v !== "string" || v.trim() !== ""))
      .map((v) => String(v))
      .join(", ");
  }
  if (field.type === "area" && typeof value === "string") {
    return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
  if (typeof value === "string") return value.trim();
  return String(value);
}

/** Whether a leaf carries any answer at all (value-shape based). */
export function surveyLeafHasValue(field: SurveyFieldish): boolean {
  return getSurveyLeafValueString(field) !== "";
}

/**
 * Raw scalar value of a leaf, for callers that need the value verbatim rather
 * than a string (search/KPI lookups). Typed here rather than at each call site
 * because the leaf `type` set is open-ended, so `field.value` isn't narrowable
 * off the `AttributeField` union — callers guard with `isScalarSurveyLeaf` first.
 */
export function getSurveyLeafRawValue(field: SurveyFieldish): string | number | boolean | null {
  return field.value as string | number | boolean | null;
}

/**
 * Whether a leaf holds a single scalar answer (not a photos/multiSelect array).
 * Completion metrics that only score scalar questions use this to keep media /
 * multi-select collections out of their denominator.
 *
 * Requires an actual `value` property so a value-less structural marker (an empty
 * `page`/`ellipsis` container that arrived with no children) isn't mistaken for a
 * question and doesn't inflate the denominator. Callers reach this after already
 * ruling out containers via {@link getSurveyChildren}.
 */
export function isScalarSurveyLeaf(field: SurveyFieldish): boolean {
  return "value" in field && !Array.isArray(field.value);
}

/**
 * Whether the field is a real leaf question — a value-carrying leaf, not a
 * container or a value-less structural marker. Unlike {@link isScalarSurveyLeaf}
 * this includes array-valued leaves (multiSelect/photos), for metrics that count
 * every question type.
 */
export function isSurveyLeafQuestion(field: SurveyFieldish): boolean {
  return getSurveyChildren(field) === null && "value" in field;
}
